"use client"

import Link from "next/link";
import { useEffect, useState } from "react"
import { useParams, usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import * as Icons from "@/components/fun-ui/icons";
import { FoldLeftIcon, FoldRightIcon } from "@/components/fun-ui/icons";
import { useGlobalConfig, useUserVip, useHelpPopup } from "@/stores/appStore"
import { DashboardLogo } from "@/components/Logo";
import { buttonVariants } from "@/components/fun-ui/Button";
import { dashboardConfig } from "@/config/dashboard"
import { CommunicateWithUs } from "@/components/CommunicateWithUs";
import { UserAccountNav } from "@/components/user-account-nav";
import { IconButton } from "@/components/icon-button";
import { SidebarNavItem } from "@/types";
import { Tip } from "../fun-ui/Tip";
import { UsageQuotasForSidebar } from "./UsageQuotas";
import { MarketingEntrance } from "./MarketingEntrance";
import { UpgradeVipButton } from "../payment/UpgradeVipButton";
import { ReceiveVipButton } from "../payment/ReceiveVipButton";
import { useSidebarCollapsed } from "@/stores/localStore";
import { HelpPopup } from "@/components/guides/HelpPopup";
import { useRef } from "react";

export function Sidebar() {
  const path = usePathname();
  const {
    collapsed,
    setCollapsed
  } = useSidebarCollapsed();
  const {
    vip: isVip,
    isLargePaymentUser,
    vipExtra,
  } = useUserVip();
  const { isOpen: helpPopupIsOpen } = useHelpPopup();

  const { paperId } = useParams();

  const [showHandle, setShowHandle] = useState<boolean>(true);
  const {
    value: globalConfig,
    fetch: fetchGlobalConfig,
  } = useGlobalConfig().globalConfig;

  useEffect(() => {
    fetchGlobalConfig();
  }, [fetchGlobalConfig]);

  useEffect(() => {
    if (paperId) {
      setCollapsed(true);
    }
  }, [paperId, setCollapsed]);

  const { sidebarNav: items, bottomNav: bottomItems, userAccountNav } = dashboardConfig;
  const helpCenterRef = useRef<HTMLDivElement>(null);

  const SidebarItem = (item: SidebarNavItem) => {
    const Icon = item.icon ? Icons[item.icon] : null
    const isExternalLink = item.href?.startsWith('http://') || item.href?.startsWith('https://');
    const Component = item.component && SidebarComponents[item.component] || (isExternalLink ? 'a' : Link);
    const linkProps = isExternalLink && item.target ? { target: item.target, rel: 'noopener noreferrer' } : (item.target ? { target: item.target, rel: 'noopener noreferrer' } : {});
    // 判断是否为"帮助中心"且 HelpPopup 已打开
    const isHelpCenter = item.title === "帮助中心";
    const isActive = path.startsWith(item.href);
    const isHelpPopupHover = isHelpCenter && helpPopupIsOpen;
    return (
      <Component
        href={!item.disabled && item.href ? item.href : ''}
        className="relative group mx-2"
        {...linkProps}
      >
        {
          collapsed ? (
            <div
              ref={isHelpCenter ? helpCenterRef : null}
              className="flex items-center justify-center py-2.5 h-10"
            >
              {
                (
                  Icon ? (item.href ?
                    <IconButton
                      className={cn(
                        isActive ? "text-primary" : "",
                      )}
                      label={item.title} side="right" Icon={Icon}
                    /> : (
                      <div
                        className={cn(
                          "h-6 w-6",
                          buttonVariants({
                            variant: "ghost",
                            size: "icon",
                          })
                        )}
                      >
                        <Icon size={item.size ?? 16} />
                      </div>
                    )) : null
                )
              }
            </div>
          ) : (
            <div
              ref={isHelpCenter ? helpCenterRef : null}
              className={cn(
                "flex items-center pl-4 py-2.5 text-sm bg-background text-muted-foreground rounded-lg hover:bg-muted space-x-4",
                isActive && "bg-muted text-primary",
                isHelpPopupHover && "bg-muted",
                item.disabled && "cursor-not-allowed opacity-80",
              )}
            >
              {
                Icon ?
                  <span className="min-w-4 min-h-4 flex justify-center"><Icon size={item.size ?? 16} /></span> : null
              }
              <span className="whitespace-nowrap">{item.title}</span>
            </div>
          )
        }
      </Component>
    );
  }
  return (
    <aside
      className={[
        "relative flex flex-col transition-all h-full",
        collapsed ? "duration-100 w-[48px]"
          : "duration-300 w-[180px]",
      ].join(' ')}
      onMouseOver={() => {
        setShowHandle(true);
      }}
      onMouseOut={() => {
        setShowHandle(false);
      }}
    >
      <div className="flex h-[60px] items-center justify-center w-full flex-shrink-0">
        <Link href="/papers" className="flex items-center justify-center">
          <DashboardLogo iconOnly={collapsed} />
        </Link>
      </div>
      <div className="flex-1 flex flex-col justify-between overflow-y-auto overflow-x-hidden">
        <div className="flex-1 flex flex-col pt-2">
          <nav className="flex flex-col space-y-2 ">
            {items.map((item, index) => <SidebarItem {...item} key={index} />)}
          </nav>
          <nav className="flex-1 flex flex-col justify-end space-y-2">
            <MarketingEntrance collapsed={collapsed} />
            {bottomItems.map((item, index) => <SidebarItem {...item} key={index} />)}
          </nav>
        </div>
        <div className={cn(
          "flex flex-col mt-2 mb-5 space-y-2",
          collapsed ? "mx-1" : "mx-2"
        )}>
          {
            (!isVip && !isLargePaymentUser && vipExtra) ? (
              <Tip label={collapsed ? "升级会员享特惠" : ""} side="right">
                <div className={cn(
                  "flex flex-1 items-center",
                  collapsed ? "justify-center p-2.5" : "px-4 py-2.5",
                )}>
                  <UpgradeVipButton
                    size="sm"
                    className={cn(
                      "flex-1",
                      collapsed ? "h-6 px-1.5" : "pl-1"
                    )}
                    iconSize={collapsed ? 12 : 16}
                    iconOnly={collapsed}
                  >
                    升级会员享特惠
                  </UpgradeVipButton>
                </div>
              </Tip>
            ) : null
          }
          {
            (!isVip && isLargePaymentUser) ? (
              <ReceiveVipButton collapsed={collapsed} />
            ) : null
          }
          <UsageQuotasForSidebar className={cn(
            "flex flex-1 items-center text-sm bg-background text-muted-foreground rounded-lg hover:bg-muted space-x-4",
            collapsed ? "justify-center p-2.5" : "px-4 py-2.5",
          )} iconOnly={collapsed} />
          <UserAccountNav
            className={cn(
              "flex flex-1 items-center p-2 text-sm bg-background text-muted-foreground rounded-lg hover:bg-muted space-x-4",
              collapsed ? "px-1.5" : "px-2"
            )}
            iconOnly={collapsed}
            navItems={userAccountNav}
          />
        </div>
      </div>
      <Tip side="right" sideOffset={8} label={collapsed ? "展开菜单" : "折叠菜单"}>
        <div
          data-show={showHandle}
          onClick={() => {
            setCollapsed(!collapsed);
          }}
          className={[
            "absolute z-10 cursor-pointer right-0 top-10 translate-x-1/2 w-[26px] h-[26px] justify-center items-center rounded-full shadow-[0_3px_8px_0_rgba(0,0,0,0.16)] bg-background flex opacity-0",
            "transition-opacity duration-300",
            "data-[show=true]:opacity-100 data-[show=false]:opacity-0",
          ].join(' ')
          }
        >
          {collapsed ? <FoldRightIcon /> : <FoldLeftIcon />}
        </div>
      </Tip>
      <HelpPopup helpCenterRef={helpCenterRef} />
    </aside>
  );
}

export const SidebarComponents = {
  CommunicateWithUs: ({ children }: { children: React.JSX.Element }) => (
    <CommunicateWithUs
      side="right"
    >
      <div className="mx-2">{children}</div>
    </CommunicateWithUs>
  ),
  Divider: () => (
    <div className="shrink-0 px-2 h-[1px]">
      <hr className="shrink-0 bg-border h-full w-full" />
    </div>
  )
}